/**
 * B2B Partnership Form
 * Two-step form with AJAX submission to ground.ajen.care API.
 * Step 1: Company name with Google Places autocomplete.
 * Step 2: Confirm/complete address details, VAT, phone, comments.
 *         Hospitality leads also choose sample + ship-to (#262 instant onboarding).
 * Success: manual block (default) or auto block when Ground answers auto_onboarded === true.
 * No page reloads — everything happens client-side via fetch().
 *
 * Test hook (no mock in the code path): call
 *   document.querySelector('b2b-partnership-form').renderSuccess({ ...response })
 * from the console to preview any success state.
 */

const B2B_API_URL = 'https://ground.ajen.care/api/b2b-lead';

class B2BPartnershipForm extends HTMLElement {
  constructor() {
    super();

    this.step1 = this.querySelector('[data-step="1"]');
    this.step2 = this.querySelector('[data-step="2"]');
    this.success = this.querySelector('[data-step="success"]');

    this.form1 = this.querySelector('#B2BPartnershipForm');
    this.form2 = this.querySelector('#B2BPartnershipFormStep2');

    this.stepIndicator = this.querySelector('[data-step-indicator]');
    this.stepCurrent = this.querySelector('[data-step-current]');

    this.companyPlacesData = null;
    this.step1Data = null;
    this.leadToken = null;
  }

  connectedCallback() {
    if (this.form1) {
      this.form1.addEventListener('submit', this.handleStep1Submit.bind(this));
    }
    if (this.form2) {
      this.form2.addEventListener('submit', this.handleStep2Submit.bind(this));
    }

    const skipBtn = this.querySelector('[data-skip]');
    if (skipBtn) {
      skipBtn.addEventListener('click', this.handleSkip.bind(this));
    }

    const backBtn = this.querySelector('[data-back]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBack.bind(this));
    }

    // Step 2: keep the country name in sync with the ISO select
    const countrySelect = this.form2 && this.form2.querySelector('[data-address-country-code]');
    if (countrySelect) {
      countrySelect.addEventListener('change', () => {
        if (countrySelect.value) {
          this.syncCountryName();
        } else {
          const nameInput = this.form2.querySelector('[data-address-country]');
          if (nameInput) nameInput.value = '';
        }
      });
    }

    // Step 2 (hospitality): ship-to toggle
    this.querySelectorAll('[data-ship-to]').forEach((radio) => {
      radio.addEventListener('change', () => this.updateShipOther());
    });

    // Re-evaluate the house-number hint while the visitor edits the address
    this.querySelectorAll('[data-address-line], [data-address-line2], [data-ship-address1], [data-ship-address2]')
      .forEach((input) => input.addEventListener('input', () => this.showHouseNumberHint()));

    this.preloadPlacesScript();
  }

  /* ------------------------------------------------------------------
   * Step 1: AJAX submit to API, then show step 2
   * ------------------------------------------------------------------ */

  async handleStep1Submit(e) {
    e.preventDefault();

    if (!this.form1.checkValidity()) {
      this.form1.reportValidity();
      return;
    }

    const button = this.form1.querySelector('.b2b-form__submit');
    this.setLoading(button, true);
    this.hideError(this.form1);

    const email = this.form1.querySelector('[name="contact[email]"]').value;
    const firstName = this.form1.querySelector('#B2BForm-first-name').value;
    const lastName = this.form1.querySelector('#B2BForm-last-name').value;
    const companyName = this.form1.querySelector('#B2BForm-company').value;
    const partnershipType = this.form1.querySelector('#B2BForm-type').value;

    const payload = {
      form_step: 'step_1',
      email: email,
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      partnership_type: partnershipType,
    };

    // Include Places address data if available
    if (this.companyPlacesData) {
      payload.address_street = this.companyPlacesData.street;
      payload.address_number = this.companyPlacesData.number;
      payload.address_postal_code = this.companyPlacesData.postal;
      payload.address_city = this.companyPlacesData.city;
      payload.address_country = this.companyPlacesData.country;
      payload.address_country_code = this.companyPlacesData.countryCode;
      payload.address_formatted = this.companyPlacesData.formatted;
    }

    try {
      const resp = await fetch(B2B_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        // lead_token binds step 2 to exactly this lead (#262); optional for backward-compat
        const data = await resp.json().catch(() => ({}));
        this.leadToken = (data && typeof data.lead_token === 'string' && data.lead_token) || null;

        // Save data for step 2 pre-fill
        this.step1Data = {
          email: email,
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
          partnership_type: partnershipType,
          places_data: this.companyPlacesData,
        };
        this.prefillStep2();
        this.showStep(2);
      } else {
        const data = await resp.json().catch(() => ({}));
        this.showError(this.form1, data.error || this.dataset.errorGeneric);
      }
    } catch (err) {
      this.showError(this.form1, this.dataset.errorConnection);
    } finally {
      this.setLoading(button, false);
    }
  }

  /* ------------------------------------------------------------------
   * Step 2: AJAX submit to API, then show success
   * ------------------------------------------------------------------ */

  async handleStep2Submit(e) {
    e.preventDefault();

    this.showHouseNumberHint();
    if (!this.form2.checkValidity()) {
      this.form2.reportValidity();
      return;
    }

    const button = this.form2.querySelector('.b2b-form__submit');
    this.setLoading(button, true);
    this.hideError(this.form2);

    const payload = this.buildStep2Payload();

    try {
      const resp = await fetch(B2B_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        // Fail-safe: any parse problem → manual success block
        const data = await resp.json().catch(() => null);
        this.renderSuccess(data);
      } else {
        this.showError(this.form2, this.dataset.errorGeneric);
      }
    } catch (err) {
      this.showError(this.form2, this.dataset.errorConnection);
    } finally {
      this.setLoading(button, false);
    }
  }

  /* ------------------------------------------------------------------
   * Step 2 payload (#262 API-contract)
   * Retail / corporate: today's fields + partnership_type (value).
   * Hospitality: + lead_token, country_code, ship_to, ship_address,
   *              sample_wanted (real boolean), locale.
   * ------------------------------------------------------------------ */

  buildStep2Payload() {
    const f2 = this.form2;
    const val = (sel) => (f2.querySelector(sel)?.value || '').trim();
    this.syncCountryName();

    const payload = {
      form_step: 'step_2',
      email: this.step1Data ? this.step1Data.email : '',
      company_name: val('[data-s2-company]'),
      address1: val('[data-address-line]'),
      address2: val('[data-address-line2]'),
      address_postal_code: val('[data-address-postal]'),
      address_city: val('[data-address-city]'),
      address_country: val('[data-address-country]'),
      company_vat_number: val('#B2BForm-vat'),
      phone: val('#B2BForm-phone'),
      comments: val('#B2BForm-comments'),
      partnership_type: this.step1Data ? this.step1Data.partnership_type : '',
    };

    if (!this.isHospitality()) return payload;

    if (this.leadToken) payload.lead_token = this.leadToken;
    // Ground derives the company-address recipient and the mail salutation from these
    payload.first_name = this.step1Data.first_name || '';
    payload.last_name = this.step1Data.last_name || '';
    payload.country_code = val('[data-address-country-code]');

    const shipTo = f2.querySelector('[data-ship-to]:checked')?.value === 'other' ? 'other' : 'company';
    payload.ship_to = shipTo;
    if (shipTo === 'other') {
      payload.ship_address = {
        name: val('[data-ship-name]'),
        company: payload.company_name,
        address1: val('[data-ship-address1]'),
        address2: val('[data-ship-address2]'),
        zip: val('[data-ship-zip]'),
        city: val('[data-ship-city]'),
        country_code: val('[data-ship-country-code]'),
        phone: val('[data-ship-phone]'),
      };
    }

    // JSON boolean — the server only accepts a real boolean here
    payload.sample_wanted = f2.querySelector('[data-sample-wanted]:checked')?.value !== 'false';
    payload.locale = this.currentLocale();

    return payload;
  }

  isHospitality() {
    return !!(this.step1Data && this.step1Data.partnership_type === 'hospitality');
  }

  currentLocale() {
    const shopifyLocale = window.Shopify && window.Shopify.locale;
    return shopifyLocale || document.documentElement.lang || 'en';
  }

  syncCountryName() {
    const select = this.form2.querySelector('[data-address-country-code]');
    const nameInput = this.form2.querySelector('[data-address-country]');
    if (!select || !nameInput) return;
    // Only overwrite when a listed country is selected; otherwise keep the Places name (unlisted countries)
    if (!select.value) return;
    const opt = select.options[select.selectedIndex];
    nameInput.value = opt ? opt.textContent.trim() : '';
  }

  updateHospitalityFields() {
    const block = this.form2.querySelector('[data-hospitality-only]');
    const countrySelect = this.form2.querySelector('[data-address-country-code]');
    const hospitality = this.isHospitality();
    if (block) block.hidden = !hospitality;
    if (countrySelect) countrySelect.required = hospitality;
    // The auto-onboarding gate needs a complete, shippable company address.
    // Without these the lead falls back to the manual path (ship_address1_missing).
    ['[data-address-line]', '[data-address-postal]', '[data-address-city]'].forEach((sel) => {
      const el = this.form2.querySelector(sel);
      if (el) el.required = hospitality;
    });
    this.updateShipOther();
  }

  /* House number hint. The auto-onboarding gate needs an address line with a
   * digit (ship_address1_no_number), but plenty of valid hospitality addresses
   * abroad have no house number at all — a country estate, a rural B&B. Blocking
   * those would trade a manual lead for no lead, so this only nudges: it shows
   * once, next to the field, and never prevents the submit. */
  showHouseNumberHint() {
    const hint = this.form2.querySelector('[data-house-number-hint]');
    if (!hint) return;
    const hasDigit = (...values) => values.some((v) => /\d/.test(v || ''));
    const value = (sel) => (this.form2.querySelector(sel)?.value || '');
    const shipOther = this.form2.querySelector('[data-ship-to]:checked')?.value === 'other';
    const missing = this.isHospitality() && (
      !hasDigit(value('[data-address-line]'), value('[data-address-line2]'))
      || (shipOther && !hasDigit(value('[data-ship-address1]'), value('[data-ship-address2]')))
    );
    hint.hidden = !missing;
    hint.textContent = missing ? (this.dataset.errorHouseNumber || '') : '';
  }

  updateShipOther() {
    const other = this.form2.querySelector('[data-ship-other]');
    if (!other) return;
    const show = this.isHospitality() && this.form2.querySelector('[data-ship-to]:checked')?.value === 'other';
    other.hidden = !show;
    ['[data-ship-name]', '[data-ship-address1]', '[data-ship-zip]', '[data-ship-city]', '[data-ship-country-code]'].forEach((sel) => {
      const el = other.querySelector(sel);
      if (el) el.required = show;
    });
  }

  /* ------------------------------------------------------------------
   * Pre-fill step 2 from step 1 data
   * ------------------------------------------------------------------ */

  prefillStep2() {
    if (!this.step1Data) return;

    // Hidden company field for form data
    const companyField = this.querySelector('[data-s2-company]');
    if (companyField) companyField.value = this.step1Data.company_name;

    // Personalize heading
    const heading = this.querySelector('[data-s2-heading]');
    if (heading && this.step1Data.first_name) {
      const template = this.dataset.personalizedHeading || '';
      heading.textContent = template.split('{first_name}').join(this.step1Data.first_name);
    }

    const hint = this.querySelector('[data-address-hint]');

    if (this.step1Data.places_data) {
      const pd = this.step1Data.places_data;
      const setVal = (sel, val) => {
        const el = this.querySelector(sel);
        if (el && val) el.value = val;
      };
      // Combine street + number into one address line
      const addressLine = [pd.street, pd.number].filter(Boolean).join(' ');
      setVal('[data-address-line]', addressLine);
      setVal('[data-address-postal]', pd.postal);
      setVal('[data-address-city]', pd.city);
      this.setCountry(pd.countryCode, pd.country);

      if (hint) hint.textContent = this.dataset.addressHintVerify;
    } else {
      if (hint) hint.textContent = this.dataset.addressHintEnter;
    }

    this.updateHospitalityFields();
  }

  // Select the ISO code in the step-2 country select (if listed); keep the Places name as fallback
  setCountry(code, name) {
    const select = this.form2.querySelector('[data-address-country-code]');
    const nameInput = this.form2.querySelector('[data-address-country]');
    if (!select) return;
    const upper = (code || '').toUpperCase();
    const hasOption = Array.from(select.options).some((o) => o.value === upper);
    select.value = hasOption ? upper : '';
    if (hasOption) {
      this.syncCountryName();
    } else if (nameInput) {
      nameInput.value = name || '';
    }
    const shipSelect = this.form2.querySelector('[data-ship-country-code]');
    if (shipSelect && hasOption && !shipSelect.value) shipSelect.value = upper;
  }

  /* ------------------------------------------------------------------
   * Skip / Back
   * ------------------------------------------------------------------ */

  handleSkip() {
    this.renderSuccess(null);
  }

  /* ------------------------------------------------------------------
   * Success screen — manual (default) or auto (#262 instant onboarding)
   * ------------------------------------------------------------------ */

  renderSuccess(resp) {
    const manual = this.success.querySelector('[data-success-manual]');
    const auto = this.success.querySelector('[data-success-auto]');
    const isAuto = !!(resp && typeof resp === 'object' && resp.auto_onboarded === true && auto);

    if (manual) manual.hidden = isAuto;
    if (auto) auto.hidden = !isAuto;
    if (isAuto) this.fillAutoSuccess(resp, auto);

    this.showStep('success');
  }

  fillAutoSuccess(resp, auto) {
    const email = resp.login_email || (this.step1Data && this.step1Data.email) || '';
    const company = (this.form2.querySelector('[data-s2-company]')?.value || (this.step1Data && this.step1Data.company_name) || '').trim();

    // Login line
    const login = auto.querySelector('[data-auto-login]');
    if (login) {
      const tpl = login.dataset.template || '';
      login.hidden = !tpl;
      if (tpl) login.innerHTML = this.fillTemplate(tpl, { email: email });
    }

    // Sample line
    const sampleLine = auto.querySelector('[data-auto-sample]');
    const sampleText = auto.querySelector('[data-auto-sample-text]');
    const changeLink = auto.querySelector('[data-auto-change-address]');
    if (sampleLine && sampleText) {
      const status = resp.sample_status;
      let html = '';
      let showLink = false;
      if (status === 'scheduled') {
        const address = this.formatShipAddress(resp.ship_address);
        html = this.fillTemplate(sampleText.dataset.template || '', { address: address });
        const day = this.formatReleaseDate(resp.sample_release_at);
        if (day && sampleText.dataset.templateRelease) {
          html += ' ' + this.fillTemplate(sampleText.dataset.templateRelease, { date: day });
        }
        showLink = true;
      } else if (status === 'declined') {
        html = this.escapeHtml(sampleText.dataset.templateDeclined || '');
      }
      sampleText.innerHTML = html;
      sampleLine.hidden = !html;
      if (changeLink) {
        changeLink.hidden = !showLink;
        changeLink.href = 'mailto:hello@ajen.care?subject=' + encodeURIComponent('Verzendadres sample ' + company);
      }
    }

    // VAT line
    const vat = auto.querySelector('[data-auto-vat]');
    if (vat) {
      const status = resp.vat_status;
      let text = '';
      if (status === 'exempt') text = vat.dataset.templateOk || '';
      else if (status === 'missing' || status === 'invalid' || status === 'unverified') text = vat.dataset.templateHint || '';
      vat.textContent = text;
      vat.hidden = !text;
    }
  }

  formatShipAddress(addr) {
    if (!addr || typeof addr !== 'object') return '';
    const cityLine = [addr.zip, addr.city].filter(Boolean).join(' ');
    return [addr.name, addr.address1, addr.address2, cityLine, addr.country_code].filter(Boolean).join(', ');
  }

  formatReleaseDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    // API geeft een volledig aware ISO-timestamp (UTC). De releasedatum is een
    // Amsterdam-kalenderdag (ops-planning) — dus altijd pinnen op Europe/Amsterdam,
    // ongeacht de tijdzone van de bezoeker's browser (anders kan bv. een 13:00u-slot
    // in Kiritimati al de volgende dag zijn, of in Adak nog de vorige).
    try {
      return new Intl.DateTimeFormat(this.currentLocale(), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'Europe/Amsterdam'
      }).format(date);
    } catch (err) {
      return date.toLocaleDateString(undefined, { timeZone: 'Europe/Amsterdam' });
    }
  }

  // Escape the template, then substitute {key} placeholders with escaped, emphasised values
  fillTemplate(template, values) {
    let html = this.escapeHtml(template);
    Object.keys(values).forEach((key) => {
      const value = '<strong>' + this.escapeHtml(values[key] || '') + '</strong>';
      html = html.split('{' + key + '}').join(value);
    });
    return html;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  handleBack() {
    this.showStep(1);
  }

  /* ------------------------------------------------------------------
   * Step transitions
   * ------------------------------------------------------------------ */

  showStep(step) {
    const steps = [this.step1, this.step2, this.success];

    steps.forEach((el) => {
      if (!el) return;
      el.classList.remove('b2b-form__step--active');
      el.classList.add('b2b-form__step--hidden');
    });

    let target;
    if (step === 'success') {
      target = this.success;
    } else if (step === 2) {
      target = this.step2;
    } else {
      target = this.step1;
    }

    if (target) {
      requestAnimationFrame(() => {
        target.classList.remove('b2b-form__step--hidden');
        target.classList.add('b2b-form__step--active');

        setTimeout(() => {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      });
    }

    if (step === 'success') {
      if (this.stepIndicator) this.stepIndicator.hidden = true;
    } else {
      if (this.stepCurrent) this.stepCurrent.textContent = step;
    }
  }

  /* ------------------------------------------------------------------
   * Loading / Error helpers
   * ------------------------------------------------------------------ */

  setLoading(button, loading) {
    if (!button) return;
    const text = button.querySelector('.b2b-form__submit-text');
    const spinner = button.querySelector('.b2b-form__submit-loading');
    button.disabled = loading;
    if (text) text.hidden = loading;
    if (spinner) spinner.hidden = !loading;
  }

  showError(form, message) {
    const errorEl = form.querySelector('[data-form-error]');
    if (!errorEl) return;
    errorEl.hidden = false;
    const p = errorEl.querySelector('p');
    if (p) p.textContent = message;
  }

  hideError(form) {
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) errorEl.hidden = true;
  }

  /* ------------------------------------------------------------------
   * Google Places Autocomplete on Company field (step 1)
   * ------------------------------------------------------------------ */

  preloadPlacesScript() {
    const apiKey = this.dataset.googleApiKey;
    if (!apiKey) return;

    if (window.google && window.google.maps && window.google.maps.places) {
      this.placesReady = true;
      this.initCompanyAutocomplete();
      return;
    }

    if (!document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      const script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + apiKey + '&libraries=places';
      script.async = true;
      document.head.appendChild(script);
    }

    const poll = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        clearInterval(poll);
        this.placesReady = true;
        this.initCompanyAutocomplete();
      }
    }, 200);
    setTimeout(() => clearInterval(poll), 10000);
  }

  initCompanyAutocomplete() {
    const companyInput = this.querySelector('#B2BForm-company');
    if (!companyInput) return;

    companyInput.autocomplete = 'off';

    const autocomplete = new google.maps.places.Autocomplete(companyInput, {
      types: ['establishment'],
      fields: ['address_components', 'formatted_address', 'name']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place) return;

      if (place.name) {
        setTimeout(() => { companyInput.value = place.name; }, 10);
      }

      if (place.address_components) {
        const components = {};
        const shortNames = {};
        place.address_components.forEach((c) => {
          c.types.forEach((type) => {
            components[type] = c.long_name;
            shortNames[type] = c.short_name;
          });
        });

        this.companyPlacesData = {
          street: components.route || '',
          number: components.street_number || '',
          postal: components.postal_code || '',
          city: components.locality || components.administrative_area_level_1 || '',
          country: components.country || '',
          countryCode: shortNames.country || '',
          formatted: place.formatted_address || ''
        };
      } else {
        this.companyPlacesData = null;
      }
    });
  }
}

customElements.define('b2b-partnership-form', B2BPartnershipForm);
